import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Menu } from 'antd';

const EDGE = 8;

/**
 * A menu that opens AT A POINT (KPR-1640): the table cell's value menu, from a
 * right-click, the Menu key or a long press. antd's Dropdown aligns to an
 * element, and a point is none; its popup also waits on an enter animation
 * before it is placed, so a menu that must be there on the gesture is drawn
 * directly: a fixed panel at the point, kept inside the window, holding an
 * antd Menu (so the rows, the arrow keys and the roles are antd's).
 *
 * It closes on Esc, on a press outside, on Tab, on scrolling or resizing (a
 * fixed panel does not follow its cell), and after a row is chosen. Focus goes
 * to the first row on open; the caller returns it to the cell on close.
 */
export default function CellMenuPopup({ x, y, items, onPick, onClose }) {
	const panelRef = useRef(null);
	const menuRef = useRef(null);
	const [place, setPlace] = useState({ left: x, top: y });

	// Keep the panel inside the window: left of the edge, above the point when
	// there is no room below.
	useLayoutEffect(() => {
		const panel = panelRef.current;
		if (!panel) return;
		const { width, height } = panel.getBoundingClientRect();
		const left = Math.max(EDGE, Math.min(x, window.innerWidth - width - EDGE));
		const below = y + height <= window.innerHeight - EDGE;
		const top = below ? y : Math.max(EDGE, y - height);
		setPlace({ left, top });
	}, [x, y, items]);

	useEffect(() => {
		const timer = setTimeout(() => menuRef.current?.focus?.(), 0);
		return () => clearTimeout(timer);
	}, []);

	const away = useCallback((event) => {
		if (panelRef.current && panelRef.current.contains(event.target)) return;
		onClose();
	}, [onClose]);
	useEffect(() => {
		document.addEventListener('mousedown', away, true);
		document.addEventListener('touchstart', away, true);
		window.addEventListener('scroll', away, true);
		window.addEventListener('resize', onClose);
		return () => {
			document.removeEventListener('mousedown', away, true);
			document.removeEventListener('touchstart', away, true);
			window.removeEventListener('scroll', away, true);
			window.removeEventListener('resize', onClose);
		};
	}, [away, onClose]);

	const handleKeyDown = (event) => {
		// The list's own keys (↑↓ move the selected item, Enter opens it) are a
		// document listener: none of what is pressed in the menu is theirs.
		event.stopPropagation();
		if (event.key === 'Escape' || event.key === 'Tab') {
			event.preventDefault();
			onClose();
		}
	};

	return createPortal(
		<div
			ref={panelRef}
			className="cell-value-menu"
			style={{ left: place.left, top: place.top }}
			onKeyDown={handleKeyDown}
			onContextMenu={event => event.preventDefault()}
			role="presentation"
		>
			<Menu
				ref={menuRef}
				items={items}
				selectable={false}
				onClick={({ key }) => onPick(key)}
			/>
		</div>,
		document.body
	);
}
